import { Arn } from 'src/utils/types'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  BaseIssue,
  BucketBackfillFailedIssue,
  ClientErrorIssue,
  DetailedIssue,
  DrsAgentNotFoundIssue,
  EksClusterAccessDeniedIssue,
  EksRequiredEndpointInaccessibleIssue,
  FailbackResourceIssue,
  KeyPairNotFoundIssue,
  MalwareScanIssue,
  MarketplaceActionRequiredIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MultipleMatchingImagesIssue,
  NewTargetPlanRequiredIssue,
  RefreshClientErrorIssue,
  S3BucketReplicationNotConfiguredIssue,
  S3RecoveryBucketNameConflictIssue,
  SyncPairPermissionIssue,
  UnconfiguredAzureBlobContainerIssue,
  UnconfiguredS3BucketReplicationIssue,
  UninstallDrsAgentIssue,
} from './issue'

/** Anything carrying an issue body plus the resource it was raised against.
 *
 *  `resourceName` is required but nullable because the two layers genuinely differ: the view
 *  enriches issues with a resource name, while `Application` has none to give, so the same
 *  issue fingerprints differently in each. Counts still agree with the page because dedup
 *  partitions by ARN and a given ARN always resolves to the same name, so duplicates of one
 *  resource strip identically. Pass the name when you have it; pass null deliberately. */
export interface DedupableIssue {
  issue: BaseIssue
  arn: Arn | null
  resourceName: string | null
}

/**
 * Strip resource-specific identifiers from a string so that issues with identical
 * messaging except for the embedded resource name/ARN produce the same fingerprint.
 *
 * The ARN goes first: a resource's name is usually a substring of its ARN, so replacing
 * the name first leaves a partly-rewritten ARN that no longer matches, stranding the
 * account id and any AWS-appended suffix in the fingerprint.
 */
function stripResourceIdentifiers(text: string, resourceName: string, arn: string): string {
  if (!text) return ''
  let result = text
  if (arn) result = result.split(arn).join('__ARN__')
  if (resourceName) result = result.split(resourceName).join('__RESOURCE__')
  return result
}

/**
 * Build a content fingerprint for an issue based on its body content.
 * Excludes resource-identifying fields (arn, resourceName, issueId) so that issues
 * with identical content but different resources share the same fingerprint.
 *
 * For known issue types with their own display component, we fingerprint on
 * issueType + category + type-specific structural fields (NOT message, since
 * message often embeds resource-specific data like domain names or ARNs).
 *
 * For unknown/default issues, message IS the displayed content so we include it.
 */
export function contentFingerprint(entry: DedupableIssue): string {
  const issue = entry.issue
  const resourceName = entry.resourceName
  const arnStr = entry.arn || ''
  const strip = (s: string) => stripResourceIdentifiers(s, resourceName, arnStr)
  // Base parts: issueType + category to differentiate issue kinds
  const parts: string[] = [issue.issueType, issue.category ?? '']

  if (issue instanceof DetailedIssue) {
    // originalErrorMessage is excluded — it carries resource-specific diagnostic output
    // (e.g. Kubernetes status conditions with per-attempt timestamps/webhooks), which is
    // what makes retry rows for one resource differ.
    parts.push(strip(issue.title))
    parts.push(strip(issue.message))
    parts.push(issue.additionalDetailsUrl ?? '')
    parts.push(issue.promptToUpdateTemplates ? '1' : '0')
    // Each script is stripped before joining so a resource name containing a quote or
    // backslash still matches (stripping serialized JSON would miss the escaped form).
    parts.push(
      issue.scripts
        ? Object.keys(issue.scripts)
            .map((lang) => `${lang}\x01${strip(issue.scripts[lang])}`)
            .join('\x02')
        : ''
    )
  } else if (issue instanceof RefreshClientErrorIssue) {
    // errorCode isn't rendered, but a different code is a different problem
    parts.push(issue.operation ?? '')
    parts.push(issue.errorCode ?? '')
    parts.push(strip(issue.errorMessage))
    parts.push(issue.resourceType ?? '')
  } else if (issue instanceof ClientErrorIssue) {
    // Covers QuotaIssue too; errorCode included for the same reason as above
    parts.push(issue.operation ?? '')
    parts.push(issue.errorCode ?? '')
    parts.push(strip(issue.errorMessage))
  } else if (issue instanceof AcmCertificateNotFoundIssue) {
    // domain is a function of the certificate, which the ARN in the dedup key already
    // pins, so all ACM cert issues share one fingerprint and group together
  } else if (issue instanceof MarketplaceAmiCantBeCopiedIssue) {
    // Renders matchCriteria (resource-specific, varies per AMI)
    parts.push(issue.matchCriteria ? JSON.stringify(Array.from(issue.matchCriteria)) : '')
  } else if (issue instanceof MultipleMatchingImagesIssue) {
    parts.push(strip(issue.message))
    parts.push(issue.matchCriteria ? JSON.stringify(Array.from(issue.matchCriteria)) : '')
  } else if (issue instanceof AmiStorageNotAccessibleIssue) {
    parts.push(issue.matchCriteria ? JSON.stringify(Array.from(issue.matchCriteria)) : '')
  } else if (issue instanceof KeyPairNotFoundIssue) {
    // keyPairName is resource-specific, so these group by type
  } else if (issue instanceof MalwareScanIssue) {
    parts.push(issue.scanStatus ?? '')
  } else if (issue instanceof MarketplaceActionRequiredIssue) {
    parts.push(issue.marketplaceUrl ?? '')
  } else if (issue instanceof EksClusterAccessDeniedIssue) {
    // Renders clusterName, delegateRoleArn, region, originalMessage
    // clusterName is resource-specific; group by delegateRoleArn + region + originalMessage
    parts.push(issue.delegateRoleArn ?? '')
    parts.push(issue.region ?? '')
    parts.push(strip(issue.originalMessage))
  } else if (issue instanceof EksRequiredEndpointInaccessibleIssue) {
    parts.push(issue.endpointUrl ?? '')
    parts.push(issue.serviceName ?? '')
  } else if (issue instanceof S3BucketReplicationNotConfiguredIssue) {
    // Each bucket has unique config — include structural fields
    parts.push(issue.replicationIamRole ?? '')
    parts.push(issue.targetBucketArn ?? '')
    parts.push(issue.kmsKeyArn ?? '')
  } else if (issue instanceof S3RecoveryBucketNameConflictIssue) {
    // Each bucket pair is unique
    parts.push(issue.primaryBucketName ?? '')
    parts.push(issue.recoveryBucketName ?? '')
  } else if (issue instanceof BucketBackfillFailedIssue) {
    parts.push(String(issue.totalCopies ?? ''))
    parts.push(String(issue.failedCopies ?? ''))
  } else if (issue instanceof DrsAgentNotFoundIssue) {
    parts.push(issue.usingSnapshots ? '1' : '0')
  } else if (issue instanceof UninstallDrsAgentIssue) {
    // ec2InstanceId is resource-specific, so these group by type
  } else if (issue instanceof FailbackResourceIssue) {
    // Resource-specific — each is unique by resource
    parts.push(issue.resourceId ?? '')
    parts.push(issue.resourceType ?? '')
  } else if (issue instanceof SyncPairPermissionIssue) {
    parts.push(issue.operation ?? '')
  } else if (issue instanceof NewTargetPlanRequiredIssue) {
    parts.push(issue.subnetMoves ? JSON.stringify(issue.subnetMoves) : '')
  } else if (issue instanceof UnconfiguredS3BucketReplicationIssue) {
    // Each bucket is unique
    parts.push(issue.bucketName ?? '')
  } else if (issue instanceof UnconfiguredAzureBlobContainerIssue) {
    parts.push(issue.storageAccountName ?? '')
  } else {
    // Default/unknown issues: message IS the displayed content
    parts.push(strip(issue.message))
    parts.push(strip(issue.title))
  }

  return parts.join('\x00')
}

/** The type-level group key, given a fingerprint already computed for this entry. */
function groupKeyFor(entry: DedupableIssue, fingerprint: string): string {
  const displayString = entry.issue.issueTypeDisplayString
  return displayString === BaseIssue.baseDisplayName ? `${BaseIssue.baseDisplayName}_${fingerprint}` : displayString
}

/**
 * The type-level grouping key used by the Issues page. Issues with the generic "Issue"
 * fallback have no meaningful type name, so they key off their content fingerprint
 * instead — structurally identical messages group together while truly different
 * ones stay separate. Only that fallback pays for a fingerprint.
 */
export function issueGroupKey(entry: DedupableIssue): string {
  const displayString = entry.issue.issueTypeDisplayString
  return displayString === BaseIssue.baseDisplayName ? groupKeyFor(entry, contentFingerprint(entry)) : displayString
}

/**
 * Collapse issues that report the same problem against the same resource. The backend
 * raises issues append-only with no dedup, so a node that re-reports on every retry
 * cycle writes one row per attempt — a single problem can occupy thousands of rows.
 *
 * Keeps the first occurrence in input order. Issue IDs are timestamp-prefixed and the API
 * returns them oldest-first within a scope, so within one scope that is the oldest; a
 * section spanning several scopes keeps whichever scope sorts first.
 */
export function dedupeIssues<T extends DedupableIssue>(issues: T[]): T[] {
  if (!issues?.length) return []
  const seen = new Set<string>()
  const kept: T[] = []
  for (const entry of issues) {
    const fingerprint = contentFingerprint(entry)
    // ARN-less issues (raised by infrastructure nodes) render one shared body with no
    // resource list, so identical ones are indistinguishable on screen and collapse too.
    const key = `${groupKeyFor(entry, fingerprint)}\x00${fingerprint}\x00${entry.arn || ''}`
    if (seen.has(key)) continue
    seen.add(key)
    kept.push(entry)
  }
  return kept
}
